import React, { useEffect, useState } from 'react';
import { Box, Link, styled, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PATH } from '../constants/routes';
import api from '../services/api';
import { showGlobalToast } from '../services/toastBus';
import Button from '../components/Button';
import DrivioLogo from '../components/DrivioLogo';
import InputField from '../components/InputField';
import OtpInput, { OTP_LENGTH } from '../components/OtpInput';
import { getThemeColors } from '../helpers/themeStyles';

const LEFT_PANEL_IMAGE_URL = `${process.env.REACT_APP_URL}/assets/login.webp`;

const STEPS = {
  EMAIL: 'email',
  OTP: 'otp',
  PASSWORD: 'password',
};

const RESEND_COOLDOWN_SECONDS = 60;

const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .trim()
    .required("L'adresse e-mail est requise.")
    .email('Entrez une adresse e-mail valide.'),
});

const resetPasswordSchema = yup.object({
  password: yup.string().required('Le mot de passe est requis.'),
  confirmPassword: yup
    .string()
    .required('La confirmation du mot de passe est requise.')
    .oneOf([yup.ref('password')], 'Les mots de passe ne correspondent pas.'),
});

const ForgotPasswordPage = styled(Box)(({ theme }) => ({
  height: '100vh',
  backgroundColor: theme.palette.background.default,
  padding: 40,
  boxSizing: 'border-box',
}));

const ForgotPasswordLayout = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'stretch',
  gap: 40,
  backgroundColor: getThemeColors(theme).authLayoutBg,
  boxSizing: 'border-box',
}));

const LeftVisualPanel = styled(Box)({
  flex: 1.2,
  height: '100%',
  borderRadius: 20,
  overflow: 'hidden',
});

const LeftVisualImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const RightAuthPanel = styled(Box)(({ theme }) => ({
  flex: 0.95,
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: 20,
  backgroundColor: getThemeColors(theme).authPanelBg,
  padding: '80px 40px',
  boxSizing: 'border-box',
}));

const RightHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
});

const RightMain = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const FormContainer = styled(Box)({
  width: '100%',
  maxWidth: 400,
});

const BackLink = styled(Link)(({ theme }) => ({
  display: 'block',
  marginBottom: 24,
  fontSize: 12,
  fontWeight: 700,
  color: getThemeColors(theme).textPrimary,
  textAlign: 'center',
  textDecoration: 'none',
  letterSpacing: 0.08,
  textTransform: 'uppercase',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 16,
  fontSize: 40,
  fontWeight: 700,
  color: getThemeColors(theme).textPrimary,
  textAlign: 'center',
  letterSpacing: 0.06,
}));

const Instruction = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  fontSize: 14,
  color: getThemeColors(theme).textPrimary,
  textAlign: 'center',
  lineHeight: 1.6,
}));

const ForgotPasswordForm = styled('form')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const OtpErrorText = styled(Typography)({
  marginTop: -8,
  fontSize: 12,
  color: '#FF6669',
  textAlign: 'center',
});

const ResendLink = styled(Link)(({ theme }) => ({
  marginTop: 8,
  fontSize: 14,
  fontWeight: 400,
  color: getThemeColors(theme).textPrimary,
  textAlign: 'center',
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    color: getThemeColors(theme).textSecondary,
  },
}));

const submitButtonSx = (theme) => {
  const colors = getThemeColors(theme);

  return {
    marginTop: 2,
    backgroundColor: colors.accent,
    color: colors.activeTextOnAccent,
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  };
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const {
    control: passwordControl,
    handleSubmit: handlePasswordFormSubmit,
    setValue: setPasswordValue,
    formState: { isSubmitting: isResettingPassword },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const sendForgotPasswordRequest = async (email) => {
    const response = await api.post('/auth/forgot-password', { email });

    if (!response?.ok) return false;

    showGlobalToast('Un code de vérification a été envoyé à votre adresse e-mail.', 'success');
    return true;
  };

  const onEmailSubmit = async (data) => {
    const success = await sendForgotPasswordRequest(data.email);

    if (!success) return;

    setSubmittedEmail(data.email);
    setOtp('');
    setOtpError('');
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setStep(STEPS.OTP);
  };

  const handleResendOtp = async () => {
    if (!submittedEmail || isResending || resendCooldown > 0) return;

    setIsResending(true);

    try {
      const success = await sendForgotPasswordRequest(submittedEmail);

      if (!success) return;

      setOtp('');
      setOtpError('');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);

    if (otpError) {
      setOtpError('');
    }
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();

    if (otp.length !== OTP_LENGTH) {
      setOtpError(`Entrez le code à ${OTP_LENGTH} chiffres.`);
      return;
    }

    setOtpError('');
    setIsVerifyingOtp(true);

    try {
      const response = await api.post('/auth/validate-otp', {
        email: submittedEmail,
        oneTimePassword: otp,
      });

      if (!response?.ok) return;

      setStep(STEPS.PASSWORD);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleBackFromOtp = () => {
    setStep(STEPS.EMAIL);
    setOtp('');
    setOtpError('');
    setResendCooldown(0);
  };

  const handleBackFromPassword = () => {
    setStep(STEPS.OTP);
    setPasswordValue('password', '');
    setPasswordValue('confirmPassword', '');
  };

  const onPasswordSubmit = async (data) => {
    const response = await api.post('/auth/reset-password', {
      email: submittedEmail,
      oneTimePassword: otp,
      password: data.password,
    });

    if (!response?.ok) return;

    showGlobalToast('Votre mot de passe a été réinitialisé avec succès.', 'success');
    navigate(PATH.SIGN_IN);
  };

  return (
    <ForgotPasswordPage>
      <ForgotPasswordLayout>
        <LeftVisualPanel
          sx={{
            '@media (max-width: 1024px)': {
              display: 'none',
            },
          }}
        >
          <LeftVisualImage src={LEFT_PANEL_IMAGE_URL} alt="Visuel de connexion" />
        </LeftVisualPanel>

        <RightAuthPanel
          sx={{
            '@media (max-width: 1024px)': {
              flex: 1,
            },
          }}
        >
          <RightHeader>
            <DrivioLogo sx={{ fontSize: 24 }} />
          </RightHeader>

          <RightMain>
            <FormContainer>
              {step === STEPS.EMAIL && (
                <>
                  <BackLink component={RouterLink} to={PATH.SIGN_IN}>
                    ← RETOUR À LA CONNEXION
                  </BackLink>

                  <Title>MOT DE PASSE OUBLIÉ</Title>

                  <Instruction>
                    Entrez votre adresse e-mail et nous vous enverrons un code de vérification pour
                    réinitialiser votre mot de passe.
                  </Instruction>

                  <ForgotPasswordForm onSubmit={handleSubmit(onEmailSubmit)} noValidate>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field, fieldState }) => (
                        <InputField
                          fullWidth
                          type="email"
                          label="E-mail"
                          name="forgot-password-email"
                          autoComplete="email"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          inputRef={field.ref}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          onClear={() => {
                            setValue('email', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                      )}
                    />

                    <Button
                      fullWidth
                      disableElevation
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      sx={submitButtonSx}
                    >
                      ENVOYER
                    </Button>
                  </ForgotPasswordForm>
                </>
              )}

              {step === STEPS.OTP && (
                <>
                  <BackLink component="button" type="button" onClick={handleBackFromOtp}>
                    ← MODIFIER L&apos;ADRESSE E-MAIL
                  </BackLink>

                  <Title>VÉRIFICATION</Title>

                  <Instruction>
                    Entrez le code à {OTP_LENGTH} chiffres envoyé à{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {submittedEmail}
                    </Box>
                    .
                  </Instruction>

                  <ForgotPasswordForm onSubmit={handleOtpSubmit} noValidate>
                    <OtpInput value={otp} onChange={handleOtpChange} error={Boolean(otpError)} />

                    {otpError ? <OtpErrorText>{otpError}</OtpErrorText> : null}

                    <ResendLink
                      component="button"
                      type="button"
                      onClick={handleResendOtp}
                      disabled={isResending || resendCooldown > 0}
                      sx={{
                        border: 'none',
                        background: 'none',
                        fontFamily: 'inherit',
                        opacity: isResending || resendCooldown > 0 ? 0.6 : 1,
                        pointerEvents: isResending || resendCooldown > 0 ? 'none' : 'auto',
                      }}
                    >
                      {resendCooldown > 0
                        ? `Renvoyer le code (${resendCooldown}s)`
                        : 'Renvoyer le code'}
                    </ResendLink>

                    <Button
                      fullWidth
                      disableElevation
                      type="submit"
                      variant="contained"
                      disabled={isVerifyingOtp}
                      sx={submitButtonSx}
                    >
                      VÉRIFIER
                    </Button>
                  </ForgotPasswordForm>
                </>
              )}

              {step === STEPS.PASSWORD && (
                <>
                  <BackLink component="button" type="button" onClick={handleBackFromPassword}>
                    ← RETOUR À LA VÉRIFICATION
                  </BackLink>

                  <Title>NOUVEAU MOT DE PASSE</Title>

                  <Instruction>
                    Choisissez un nouveau mot de passe pour votre compte{' '}
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {submittedEmail}
                    </Box>
                    .
                  </Instruction>

                  <ForgotPasswordForm
                    onSubmit={handlePasswordFormSubmit(onPasswordSubmit)}
                    noValidate
                  >
                    <Controller
                      name="password"
                      control={passwordControl}
                      render={({ field, fieldState }) => (
                        <InputField
                          fullWidth
                          type="password"
                          withPasswordToggle
                          label="Nouveau mot de passe"
                          name="forgot-password-new"
                          autoComplete="new-password"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          inputRef={field.ref}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          onClear={() => {
                            setPasswordValue('password', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="confirmPassword"
                      control={passwordControl}
                      render={({ field, fieldState }) => (
                        <InputField
                          fullWidth
                          type="password"
                          withPasswordToggle
                          label="Confirmer le mot de passe"
                          name="forgot-password-confirm"
                          autoComplete="new-password"
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          inputRef={field.ref}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          onClear={() => {
                            setPasswordValue('confirmPassword', '', {
                              shouldValidate: true,
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                        />
                      )}
                    />

                    <Button
                      fullWidth
                      disableElevation
                      type="submit"
                      variant="contained"
                      disabled={isResettingPassword}
                      sx={submitButtonSx}
                    >
                      RÉINITIALISER
                    </Button>
                  </ForgotPasswordForm>
                </>
              )}
            </FormContainer>
          </RightMain>
        </RightAuthPanel>
      </ForgotPasswordLayout>
    </ForgotPasswordPage>
  );
};

export default ForgotPassword;
